import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import CompanySelector from './components/CompanySelector';
import ErrorBoundary from '../components/ErrorBoundary';
import { adminNotificationsApi, handleNotificationError } from '../utils/notificationsApi';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';
import './styles/mobile-buttons.css';
import './styles/admin-table-vis.css';

// Bildirim tiplerine göre ikon ve renk eşlemesi
const NOTIF_TYPE_MAP = {
  INFO:              { icon: 'fa-info-circle',          color: '#17a2b8', bg: 'rgba(23,162,184,0.10)' },
  WARNING:           { icon: 'fa-exclamation-triangle',  color: '#e67e22', bg: 'rgba(230,126,34,0.10)' },
  ERROR:             { icon: 'fa-times-circle',          color: '#dc3545', bg: 'rgba(220,53,69,0.10)' },
  SUCCESS:           { icon: 'fa-check-circle',          color: '#28a745', bg: 'rgba(40,167,69,0.10)' },
  DELIVERY_FAILED:   { icon: 'fa-exclamation-circle',    color: '#dc3545', bg: 'rgba(220,53,69,0.10)' },
  DELIVERY_RETURNED: { icon: 'fa-undo',                  color: '#e67e22', bg: 'rgba(230,126,34,0.10)' },
};

const AdminLayout = ({ children }) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { activeCompany } = useCompany();

  const sidebarRef = useRef(null);
  const toggleButtonRef = useRef(null);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);
  const [touchStartY, setTouchStartY] = useState(0);
  const [touchEndY, setTouchEndY] = useState(0);
  const mainContentRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);

  const menuItems = [
    {
      title: 'Dashboard',
      icon: 'fas fa-chart-line',
      path: '/admin',
      roles: ['SUPER_ADMIN', 'COMPANY_ADMIN']
    },
    {
      title: 'Şirketler',
      icon: 'fas fa-building',
      path: '/admin/companies',
      roles: ['SUPER_ADMIN']
    },
    {
      title: 'Ürünler',
      icon: 'fas fa-box',
      path: '/admin/products',
      roles: ['SUPER_ADMIN', 'COMPANY_ADMIN']
    },
    {
      title: 'Abonelikler',
      icon: 'fas fa-sync',
      path: '/admin/subscriptions',
      roles: ['SUPER_ADMIN', 'COMPANY_ADMIN']
    },
    {
      title: 'Siparişler',
      icon: 'fas fa-shopping-cart',
      path: '/admin/orders',
      roles: ['SUPER_ADMIN', 'COMPANY_ADMIN', 'SHIPPING_STAFF']
    },
    {
      title: 'Kullanıcılar',
      icon: 'fas fa-users',
      path: '/admin/users',
      roles: ['SUPER_ADMIN', 'COMPANY_ADMIN']
    },
    {
      title: 'İndirim Kodları',
      icon: 'fas fa-tags',
      path: '/admin/discount-codes',
      roles: ['SUPER_ADMIN', 'COMPANY_ADMIN']
    },
    {
      title: 'Kargo Firmaları',
      icon: 'fas fa-truck',
      path: '/admin/carriers',
      roles: ['SUPER_ADMIN', 'COMPANY_ADMIN', 'SHIPPING_STAFF']
    },
    {
      title: 'E-posta Markalama',
      icon: 'fas fa-envelope-open-text',
      path: '/admin/email-branding',
      roles: ['SUPER_ADMIN', 'COMPANY_ADMIN']
    },
    {
      title: 'Şirket Ayarları',
      icon: 'fas fa-cog',
      path: '/admin/settings',
      roles: ['SUPER_ADMIN', 'COMPANY_ADMIN']
    },
    {
      title: 'Denetim Kayıtları',
      icon: 'fas fa-history',
      path: '/admin/audit-logs',
      roles: ['SUPER_ADMIN', 'COMPANY_ADMIN']
    }
  ];

  // Kullanıcının rolüne göre menü öğelerini filtrele
  const filteredMenuItems = menuItems.filter(item =>
    item.roles.includes(user?.role)
  );

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Notifications
  const fetchUnreadCount = async () => {
    try {
      const res = await adminNotificationsApi.unreadCount();
      if (res?.success) {
        setUnreadCount(res.count || res.unread || 0);
      }
    } catch (error) {
      handleNotificationError(error, 'Bildirim sayısı alınamadı');
    }
  };

  const fetchRecentNotifications = async () => {
    try {
      setNotifLoading(true);
      const res = await adminNotificationsApi.list(1, 5);
      if (res?.success) {
        setNotifications(res.data || res.notifications || []);
      }
    } catch (error) {
      handleNotificationError(error, 'Bildirimler alınamadı');
    } finally {
      setNotifLoading(false);
    }
  };

  // Mark all notifications as read when bell icon is clicked
  const handleBellClick = async () => {
    fetchRecentNotifications();
    if (unreadCount > 0) {
      try {
        await adminNotificationsApi.markAllRead();
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        setUnreadCount(0);
      } catch (error) {
        console.warn('Failed to mark all notifications as read', error);
      }
    }
  };

  // Navigate to related resource on click
  const handleNotificationClick = (notification) => {
    if (notification.orderId) {
      navigate(`/admin/orders?orderId=${notification.orderId}`);
    } else if (notification.subscriptionId) {
      navigate(`/admin/subscriptions?subscriptionId=${notification.subscriptionId}`);
    } else {
      navigate('/admin/notifications');
    }
  };

  useEffect(() => {
    if (!user) return;

    fetchUnreadCount();
    fetchRecentNotifications();

    // Polling: Check for new notifications every 30 seconds
    const pollInterval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);

    // Event listener: Instant refresh when admin actions complete
    const handleRefreshEvent = () => {
      fetchUnreadCount();
      fetchRecentNotifications();
    };
    window.addEventListener('refreshNotifications', handleRefreshEvent);

    return () => {
      clearInterval(pollInterval);
      window.removeEventListener('refreshNotifications', handleRefreshEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Close mobile sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobileSidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        toggleButtonRef.current &&
        !toggleButtonRef.current.contains(event.target)
      ) {
        setIsMobileSidebarOpen(false);
      }
    };

    // Add event listener when the sidebar is open
    if (isMobileSidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileSidebarOpen]);

  // Close mobile sidebar when route changes
  useEffect(() => {
    if (isMobileSidebarOpen) {
      setIsMobileSidebarOpen(false);
    }
  }, [location.pathname]);

  // Handle touch events for swipe gestures
  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
    setTouchStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    setTouchEndX(e.touches[0].clientX);
    setTouchEndY(e.touches[0].clientY);
  };

  const handleTouchEnd = useCallback(() => {
    // Minimum swipe distance threshold (in pixels) - Reduced for better sensitivity
    const MIN_SWIPE_DISTANCE = 50;
    // Edge zone width (in pixels from left edge) where swipe gesture is detected - Increased for easier access
    const EDGE_ZONE_WIDTH = 40;

    // If sidebar is closed and swipe starts from edge
    if (!isMobileSidebarOpen && touchStartX <= EDGE_ZONE_WIDTH) {
      // Right swipe (from left edge)
      if (touchEndX - touchStartX > MIN_SWIPE_DISTANCE) {
        // Check if it's not a diagonal swipe (which could be a back gesture on iOS)
        // This helps differentiate between horizontal menu swipes and iOS back gestures
        const touchMoveY = Math.abs(touchEndY - touchStartY);
        if (touchMoveY < 40) { // Threshold for vertical movement
          setIsMobileSidebarOpen(true);
        }
      }
    }

    // Reset touch positions
    setTouchStartX(0);
    setTouchEndX(0);
    setTouchStartY(0);
    setTouchEndY(0);
  }, [touchStartX, touchEndX, touchStartY, touchEndY, isMobileSidebarOpen, setIsMobileSidebarOpen]);

  // Handle sidebar touch events for closing
  const handleSidebarTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
    setTouchStartY(e.touches[0].clientY);
  };

  const handleSidebarTouchMove = (e) => {
    setTouchEndX(e.touches[0].clientX);
    setTouchEndY(e.touches[0].clientY);
  };

  const handleSidebarTouchEnd = useCallback(() => {
    // Minimum swipe distance threshold (in pixels)
    // Minimum swipe distance threshold (in pixels) - Reduced for better sensitivity
    const MIN_SWIPE_DISTANCE = 50;

    // Left swipe (close sidebar)
    if (touchStartX - touchEndX > MIN_SWIPE_DISTANCE) {
      // Check if it's not a diagonal swipe 
      const touchMoveY = Math.abs(touchEndY - touchStartY);
      if (touchMoveY < 40) { // Threshold for vertical movement
        setIsMobileSidebarOpen(false);
      }
    }

    // Reset touch positions
    setTouchStartX(0);
    setTouchEndX(0);
    setTouchStartY(0);
    setTouchEndY(0);
  }, [touchStartX, touchEndX, touchStartY, touchEndY, setIsMobileSidebarOpen]);

  return (
    <div className="admin-layout d-flex">
      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''} ${isMobileSidebarOpen ? 'show' : ''}`}
        onTouchStart={handleSidebarTouchStart}
        onTouchMove={handleSidebarTouchMove}
        onTouchEnd={handleSidebarTouchEnd}
      >
        {/* Header */}
        <div className="sidebar-header">
          <Link
            to={user?.role === 'SHIPPING_STAFF' ? '/admin/orders' : '/admin'}
            className="sidebar-brand text-decoration-none"
          >
            <div className="sidebar-brand-icon">
              <i className="fas fa-layer-group"></i>
            </div>
            {!isSidebarCollapsed && (
              <span className="sidebar-brand-name">
                {activeCompany?.name || user?.companyName || '?'}
              </span>
            )}
          </Link>
          <button
            className="sidebar-toggle d-none d-md-flex"
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            title={isSidebarCollapsed ? 'Genişlet' : 'Daralt'}
          >
            <i className={`fas fa-chevron-${isSidebarCollapsed ? 'right' : 'left'}`}></i>
          </button>
          <button
            className="sidebar-toggle d-flex d-md-none"
            onClick={() => setIsMobileSidebarOpen(false)}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Menu */}
        <nav className="sidebar-menu">
          {filteredMenuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`menu-item ${isActive ? 'active' : ''}`}
                title={isSidebarCollapsed ? item.title : ''}
              >
                <span className="menu-icon">
                  <i className={item.icon}></i>
                </span>
                {!isSidebarCollapsed && <span className="menu-label">{item.title}</span>}
                {isActive && !isSidebarCollapsed && <span className="menu-active-dot"></span>}
              </Link>
            );
          })}
        </nav>

        {/* Footer / User */}
        <div className="sidebar-footer">
          <div className="dropdown dropup">
            <button
              className="sidebar-user-btn"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <div className="sidebar-avatar">
                {user?.name ? user.name.charAt(0).toUpperCase() : <i className="fas fa-user"></i>}
              </div>
              {!isSidebarCollapsed && (
                <div className="sidebar-user-info">
                  <span className="sidebar-user-name">{user?.name}</span>
                  <span className="sidebar-user-role">{user?.role}</span>
                </div>
              )}
            </button>
            <ul className="dropdown-menu shadow border-0 mb-2" style={{ minWidth: '180px' }}>
              <li>
                <Link className="dropdown-item" to="/admin/profile">
                  <i className="fas fa-user-circle me-2 text-muted"></i>Profil Ayarları
                </Link>
              </li>
              <li><hr className="dropdown-divider my-1" /></li>
              <li>
                <button className="dropdown-item text-danger" onClick={handleLogout}>
                  <i className="fas fa-sign-out-alt me-2"></i>Çıkış Yap
                </button>
              </li>
            </ul>
          </div>
        </div>

        <style>{`
          /* ── Sidebar shell ──────────────────────────────────────── */
          .sidebar {
            width: 256px;
            height: 100vh;
            position: fixed;
            top: 0; left: 0;
            z-index: 1050;
            display: flex;
            flex-direction: column;
            background: #0f172a;
            border-right: 1px solid rgba(255,255,255,0.06);
            transition: width 0.25s cubic-bezier(0.4,0,0.2,1);
          }
          .sidebar.collapsed { width: 68px; }

          /* ── Header ─────────────────────────────────────────────── */
          .sidebar-header {
            height: 68px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0 14px;
            border-bottom: 1px solid rgba(255,255,255,0.06);
            flex-shrink: 0;
          }
          .sidebar-brand {
            display: flex;
            align-items: center;
            gap: 10px;
            min-width: 0;
            flex: 1;
          }
          .sidebar-brand-icon {
            width: 34px; height: 34px;
            border-radius: 9px;
            background: linear-gradient(135deg, #334155 0%, #0f172a 100%);
            border: 1px solid rgba(255,255,255,0.1);
            display: flex; align-items: center; justify-content: center;
            color: #e2e8f0;
            font-size: 0.85rem;
            flex-shrink: 0;
            box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          }
          .sidebar-brand-name {
            font-size: 0.9375rem;
            font-weight: 700;
            color: #f1f5f9;
            letter-spacing: -0.3px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .sidebar-toggle {
            width: 28px; height: 28px;
            border: none;
            background: rgba(255,255,255,0.06);
            border-radius: 7px;
            color: #64748b;
            display: flex; align-items: center; justify-content: center;
            cursor: pointer;
            flex-shrink: 0;
            transition: background 0.15s, color 0.15s;
            font-size: 0.7rem;
          }
          .sidebar-toggle:hover { background: rgba(255,255,255,0.10); color: #94a3b8; }

          /* ── Nav menu ───────────────────────────────────────────── */
          .sidebar-menu {
            flex: 1;
            overflow-y: auto;
            overflow-x: hidden;
            padding: 10px 10px;
            display: flex;
            flex-direction: column;
            gap: 2px;
          }
          .sidebar-menu::-webkit-scrollbar { width: 4px; }
          .sidebar-menu::-webkit-scrollbar-track { background: transparent; }
          .sidebar-menu::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 4px; }

          .menu-item {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 9px 10px;
            border-radius: 9px;
            text-decoration: none;
            color: #64748b;
            font-size: 0.875rem;
            font-weight: 500;
            transition: background 0.15s, color 0.15s;
            position: relative;
            white-space: nowrap;
          }
          .menu-item:hover {
            background: rgba(255,255,255,0.06);
            color: #cbd5e1;
          }
          .menu-item.active {
            background: rgba(37,99,235,0.12);
            color: #93c5fd;
          }
          .menu-icon {
            width: 28px; height: 28px;
            display: flex; align-items: center; justify-content: center;
            border-radius: 7px;
            flex-shrink: 0;
            font-size: 0.875rem;
            transition: background 0.15s;
          }
          .menu-item.active .menu-icon {
            background: rgba(37,99,235,0.2);
            color: #7cb3f8;
          }
          .menu-label { flex: 1; }
          .menu-active-dot {
            width: 6px; height: 6px;
            border-radius: 50%;
            background: #3b82f6;
            flex-shrink: 0;
          }

          /* ── Footer ─────────────────────────────────────────────── */
          .sidebar-footer {
            padding: 10px;
            border-top: 1px solid rgba(255,255,255,0.06);
            flex-shrink: 0;
          }
          .sidebar-user-btn {
            width: 100%;
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 8px 8px;
            border: none;
            background: rgba(255,255,255,0.04);
            border-radius: 9px;
            cursor: pointer;
            text-align: left;
            transition: background 0.15s;
          }
          .sidebar-user-btn:hover { background: rgba(255,255,255,0.08); }
          .sidebar-avatar {
            width: 32px; height: 32px;
            border-radius: 8px;
            background: linear-gradient(135deg, #334155 0%, #475569 100%);
            display: flex; align-items: center; justify-content: center;
            color: #e2e8f0;
            font-size: 0.8rem;
            font-weight: 700;
            flex-shrink: 0;
          }
          .sidebar-user-info {
            display: flex;
            flex-direction: column;
            min-width: 0;
            flex: 1;
          }
          .sidebar-user-name {
            font-size: 0.8125rem;
            font-weight: 600;
            color: #e2e8f0;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .sidebar-user-role {
            font-size: 0.7rem;
            color: #475569;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          /* ── Mobile ─────────────────────────────────────────────── */
          @media (max-width: 768px) {
            .sidebar {
              width: 272px !important;
              transform: translateX(-100%);
              transition: transform 0.25s cubic-bezier(0.4,0,0.2,1);
            }
            .sidebar.show {
              transform: translateX(0);
              box-shadow: 12px 0 32px rgba(0,0,0,0.4);
            }
          }
        `}</style>
      </div>

      {/* Main Content */}
      <div
        className="main-content flex-grow-1 d-flex flex-column"
        ref={mainContentRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          marginLeft: isSidebarCollapsed ? '70px' : '260px',
          minHeight: '100vh',
          transition: 'margin-left 0.3s',
          width: isSidebarCollapsed ? 'calc(100% - 70px)' : 'calc(100% - 260px)',
          maxWidth: isSidebarCollapsed ? 'calc(100% - 70px)' : 'calc(100% - 260px)',
          boxSizing: 'border-box',
          overflowX: 'clip'
        }}
      >
        {/* Top Header */}
        <header className="bg-white border-bottom sticky-top px-4 py-2 shadow-sm d-flex align-items-center justify-content-between" style={{ height: '70px', zIndex: 1040 }}>
          <div className="d-flex align-items-center">
            <button
              className="btn btn-link text-dark d-md-none me-3 p-0"
              onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
            >
              <i className="fas fa-bars fs-5"></i>
            </button>

            {/* Empty spacer where search was */}
            <div className="d-none d-md-block"></div>
          </div>

          <div className="d-flex align-items-center gap-2 gap-sm-3">
            <div>
              <CompanySelector />
            </div>
            <Link
              to="/products"
              className="btn btn-outline-primary btn-sm d-flex align-items-center"
              style={{ whiteSpace: 'nowrap' }}
            >
              <i className="fas fa-home me-md-2"></i>
              <span className="d-none d-md-inline">Kullanıcı Dashboard</span>
            </Link>

            {/* Notifications */}
            <div className="dropdown">
              <button
                className="btn btn-light position-relative rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: '40px', height: '40px' }}
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                onClick={handleBellClick}
              >
                <i className="far fa-bell text-secondary"></i>
                {unreadCount > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-light">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              <ul className="dropdown-menu dropdown-menu-end p-0 shadow-sm border-0" style={{ width: '320px', maxHeight: '400px', overflowY: 'auto' }}>
                <li className="p-3 border-bottom d-flex justify-content-between align-items-center bg-light">
                  <h6 className="mb-0 fw-bold">Bildirimler</h6>
                  <Link to="/admin/notifications" className="small text-decoration-none">Tümü</Link>
                </li>
                {notifLoading ? (
                  <li className="p-4 text-center"><div className="spinner-border spinner-border-sm text-primary"></div></li>
                ) : notifications.length === 0 ? (
                  <li className="p-4 text-center text-muted small">Bildiriminiz yok</li>
                ) : (
                  notifications.map(n => {
                    const typeConf = NOTIF_TYPE_MAP[n.type] || NOTIF_TYPE_MAP.INFO;
                    return (
                      <li key={n.id} className="border-bottom">
                        <div
                          className={`dropdown-item py-2 px-3 d-flex align-items-start gap-2 ${!n.isRead ? '' : ''}`}
                          style={{ whiteSpace: 'normal', cursor: 'pointer', backgroundColor: !n.isRead ? typeConf.bg : 'transparent' }}
                          onClick={() => handleNotificationClick(n)}
                          role="button"
                        >
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 mt-1"
                            style={{ width: 28, height: 28, backgroundColor: typeConf.bg }}
                          >
                            <i className={`fas ${typeConf.icon}`} style={{ color: typeConf.color, fontSize: '0.75rem' }}></i>
                          </div>
                          <div className="flex-grow-1 min-w-0">
                            <p className="mb-0 small fw-semibold text-dark" style={{ lineHeight: 1.3 }}>{n.title}</p>
                            <p className="mb-0 text-muted text-truncate-2" style={{ fontSize: '0.78rem', lineHeight: 1.3 }}>{n.message}</p>
                            <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                              {n.createdAt ? formatDistanceToNow(new Date(n.createdAt), { addSuffix: true, locale: tr }) : ''}
                            </span>
                          </div>
                          {!n.isRead && (
                            <span className="rounded-circle flex-shrink-0 mt-2" style={{ width: 7, height: 7, backgroundColor: typeConf.color }}></span>
                          )}
                        </div>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="p-2 p-md-4 flex-grow-1 bg-light">
          <ErrorBoundary>
            {children}
          </ErrorBoundary>
        </div>

        {/* Mobile Sidebar Overlay */}
        {isMobileSidebarOpen && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 bg-black bg-opacity-50 d-md-none"
            style={{ zIndex: 1045 }}
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}

        <style>{`
          @media (max-width: 768px) {
            .main-content { 
              margin-left: 0 !important; 
              width: 100% !important;
              max-width: 100% !important;
            }
          }
          .bg-primary-soft { background-color: rgba(13, 110, 253, 0.1); }
          .text-truncate-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
        `}</style>
      </div>
    </div>
  );
};

export default AdminLayout; 